import { api } from "../scripts/api.js";

const esc = (s) =>
   String(s ?? "").replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
   );

/*
The Games tab: run a hard-timed competition round.

A GAME is a launch instance of an assignment. Creating one mints a fresh join
code; the student's stamped file carries that code, so a pre-filled old copy of
the same case cannot link to the live round.

Everything here was already reachable over the API and had no UI at all, which
meant driving a live classroom round from the browser console. The controls are
deliberately large and few, because they get used standing at the front of a
room with thirty people waiting.

THE CLOCK IS NOT OURS. The server owns it. This polls /games?class_id= and
renders remaining_secs; between polls it ticks locally so the countdown looks
smooth, but every poll overwrites that, and no decision is ever taken from the
local number. Same discipline as the pane.

TWO SCORES. After the buzzer the results table shows the frozen game score and
the still-moving practice score in separate columns, because they now genuinely
differ: the buzzer ends the game, not the assignment, and a student may keep
working afterwards for practice credit (David, 2026-08-20).
*/

// Superseded by pollInterval(): 1s while a game is live (matching the pane, so
// the projected clock and the students' clocks agree), 5s when nothing is
// running. Kept as the floor/documentation of the old fixed cadence.
const POLL_MS = 2000;   // eslint-disable-line no-unused-vars

const fmtClock = (secs) => {
   const s = Math.max(0, Math.floor(Number(secs) || 0));
   return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

const STATE_LABEL = {
   scheduled: "Not started",
   open: "Live",
   paused: "Paused",
   closed: "Finished",
};

class AppGames extends HTMLElement {
   connectedCallback() {
      this.games = [];
      this.classes = [];
      this.assignments = [];
      this.classId = null;
      this.openResults = null;   // session_id whose results are expanded
      /* Team rows whose members are expanded, by team_id. In memory only, and
         folded into resultsSig below so a toggle actually repaints - the same
         cache that caused the 8/27 "stuck on Loading" bug would otherwise
         swallow it. */
      this.openTeams = new Set();

      /* Results are shown by nickname unless the instructor asks otherwise. In
         memory only, never persisted, so leaving the view or reloading returns
         the panel to nicknames - it is on a projector during a live game. */
      this.reveal = false;

      this.innerHTML = `
         <section id="games">
            <header class="classes-header">
               <h1>Games</h1>
            </header>

            <div class="assignments-actions">
               <label class="games-class-pick">Class
                  <select id="games-class"></select>
               </label>
               <button class="btn-primary" id="btn-new-game">New game</button>
               <button class="btn-secondary" id="btn-refresh-games">Refresh</button>
            </div>

            <p class="msg" id="games-msg"></p>
            <div id="games-list"></div>
         </section>

         <dialog class="class-dialog" id="new-game-dialog">
            <form method="dialog" id="new-game-form">
               <h2>New game</h2>
               <label>Assignment
                  <select name="assignment_id" id="ng-assignment" required></select>
               </label>
               <label>Length (minutes)
                  <input name="minutes" id="ng-minutes" type="number" min="1" max="600"
                         step="1" value="30" required>
               </label>
               <label>Name (optional)
                  <input name="title" id="ng-title" maxlength="255"
                         placeholder="Defaults to the assignment title">
               </label>
               <label>When time is up
                  <select name="lock_mode" id="ng-lock">
                     <option value="blackout" selected>Black the sheet out briefly, then let them keep working</option>
                     <option value="none">Do nothing visible</option>
                  </select>
               </label>
               <label class="ng-check">
                  <input type="checkbox" name="attach_on_close" id="ng-attach" checked>
                  When the game ends, add this assignment to the class as practice
               </label>
               <p class="tv-desc">
                  The game score freezes at the buzzer. Students can keep working afterwards,
                  and that work counts toward their practice score only.
               </p>
               <div class="msg err" id="new-game-error"></div>
               <div class="dialog-actions">
                  <button type="submit" class="btn-primary" id="btn-create-game">Create</button>
                  <button type="button" class="btn-secondary" id="btn-cancel-game">Cancel</button>
               </div>
            </form>
         </dialog>
      `;

      this.dialog = this.querySelector("#new-game-dialog");
      this.querySelector("#btn-cancel-game").addEventListener("click", () => this.dialog.close());
      // A refusal arms "Create anyway" for ONE assignment. Picking a different
      // assignment disarms it, so an override can never carry over to a file
      // it was not shown for.
      this.querySelector("#ng-assignment").addEventListener("change", () => this.disarmForce());
      this.querySelector("#new-game-form").addEventListener("submit", (e) => this.onCreate(e));
      this.querySelector("#btn-new-game").addEventListener("click", () => this.openNewGame());
      this.querySelector("#btn-refresh-games").addEventListener("click", () => this.loadGames());
      this.querySelector("#games-class").addEventListener("change", (e) => {
         this.classId = Number(e.target.value) || null;
         this.applyClassMode();
         this.openResults = null;
         this.listSig = null;        // different class, nothing on screen survives
         this.resultsSig = null;
         this.loadGames();
      });

      this.querySelector("#games-list").addEventListener("click", (e) => {
         const btn = e.target.closest("button[data-action]");
         if (!btn || btn.disabled) return;
         const id = Number(btn.dataset.id);
         const action = btn.dataset.action;
         if (action === "results") this.toggleResults(id);
         else if (action === "team") this.toggleTeam(id, Number(btn.dataset.session));
         else if (action === "copy-code") this.copyCode(btn.dataset.code, btn);
         else if (action === "delete") this.deleteGame(id, btn);
         else if (action === "scoreboard" || action === "obs") this.openBoard(id, action === "obs");
         else this.transition(id, action, btn);
      });

      /* The click delegation above matches `button[data-action]` only, so the
         real-names checkbox needs its own listener - it is an <input>, and a
         change event, not a click. */
      this.querySelector("#games-list").addEventListener("change", (e) => {
         const box = e.target.closest('input[data-action="reveal"]');
         if (!box) return;
         this.reveal = box.checked;
         this.loadResults(Number(box.dataset.id));
      });
   }

   show() {
      if (!this.loaded) {
         this.loaded = true;
         this.loadClasses();
      }
      this.startPolling();
   }

   /* The router (scripts/web-app.js initRouter) only ever sets `hidden` on the
      view wrapper — it calls show() on the way in and NOTHING on the way out.
      So there is no hide() hook to rely on, and the poll has to check for
      itself whether anyone is looking. hide() is kept for the day the router
      grows one. */
   hide() { this.stopPolling(); }
   disconnectedCallback() { this.stopPolling(); }

   isVisible() {
      const view = this.closest("[data-view]");
      return !(view && view.hidden);
   }

   /*
      ADAPTIVE POLL. The pane polls the server every 1s; this view polled every
      2s, so the projected clock could sit up to a second behind the clock the
      students were watching. Both tick locally between polls, and the server owns
      the real clock, so the visible gap is bounded by the SLOWER poll.

      Matching the pane at 1s while a game is actually running closes it, and
      backing off to 5s when nothing is live is a net REDUCTION in traffic - this
      view used to poll every 2s all day whether or not a game existed.

      Some drift is irreducible: two machines, two render loops, one network. It
      is now under a second, which is invisible on a projector.
   */
   pollInterval() {
      const live = this.games.some((g) => g.state === "open" || g.state === "paused");
      return live ? 1000 : 5000;
   }

   startPolling() {
      if (this.pollTimer != null) return;
      const arm = (ms) => {
         this.pollMs = ms;
         this.pollTimer = window.setInterval(async () => {
            await this.loadGames(true);
            // Re-arm only when the cadence should change, so a running game does
            // not tear down and rebuild its timer every second.
            const want = this.pollInterval();
            if (want !== this.pollMs) {
               window.clearInterval(this.pollTimer);
               this.pollTimer = null;
               arm(want);
            }
         }, ms);
      };
      arm(this.pollInterval());
      this.tickTimer = window.setInterval(() => this.tick(), 1000);
   }

   stopPolling() {
      if (this.pollTimer != null) { window.clearInterval(this.pollTimer); this.pollTimer = null; }
      if (this.tickTimer != null) { window.clearInterval(this.tickTimer); this.tickTimer = null; }
   }

   /* Local smoothing only. Never decides anything; the next poll overwrites it. */
   tick() {
      if (!this.isVisible()) return;
      let changed = false;
      for (const g of this.games) {
         if (g.state === "open" && g.remaining_secs > 0) { g.remaining_secs -= 1; changed = true; }
      }
      if (changed) this.paintClocks();
   }

   /*
      TA MODE, per class (2026-09-16). On a class where this person sits as a TA
      they may drive a game but not create or delete one, so both are hidden.
      Presentation only: the server refuses create/delete for a TA regardless.
   */
   /*
   Scoreboard + OBS from a game card (2026-09-29, for the FEA demo). The game's
   board IS the ordinary Scoreboard for its (class, assignment): a live game
   grades into the same score table the board reads. These buttons just take the
   instructor there with the right pair selected, and "OBS link" opens the
   overlay dialog, whose Game clock option follows this game. Only offered on a
   page that actually has a Scoreboard view (not the student page).
   */
   hasScoreboardView() {
      return !!document.querySelector('[data-view="scoreboard"]');
   }

   openBoard(sessionId, obs) {
      const g = (this.games || []).find((x) => x.session_id === sessionId);
      if (!g) return;
      location.hash = `scoreboard/${g.class_id}/${g.assignment_id}${obs ? "/obs" : ""}`;
   }

   isTaHere() {
      const c = (this.classes || []).find((x) => Number(x.class_id) === Number(this.classId));
      return !!c && c.my_role === "ta";
   }

   applyClassMode() {
      const btn = this.querySelector("#btn-new-game");
      if (btn) btn.hidden = this.isTaHere();
   }

   setMsg(text, kind = "") {
      const el = this.querySelector("#games-msg");
      el.textContent = text;
      el.className = `msg${kind ? " " + kind : ""}`;
   }

   async loadClasses() {
      try {
         // /list-classes, not /classes — the latter does not exist. Same call
         // classes.js makes.
         const res = await api("/list-classes");
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || "failed");
         // Archived classes are still listed; you do not run a game in one.
         // A class where this person is only a TA is listed only if that class
         // lets its TAs run games (2026-09-16, lib/ta-permissions.js); otherwise
         // every control on it would 403.
         this.classes = (data.classes || []).filter((c) => c.active !== false && !c.archived)
            .filter((c) => c.my_role !== "ta" || (c.ta_permissions && c.ta_permissions.run_games));
         const sel = this.querySelector("#games-class");
         if (!this.classes.length) {
            sel.innerHTML = `<option value="">No classes</option>`;
            this.querySelector("#games-list").innerHTML =
               `<p class="empty">${this.getAttribute("role") === "ta"
                  ? "None of your classes lets TAs run games."
                  : "You do not have a class yet. Create one first."}</p>`;
            return;
         }
         /* `c.name` does not exist. /list-classes returns course_code, section,
            title and term - never `name` - so every option rendered with a BLANK
            label: the dropdown looked empty even though it was full, and the
            first class was silently selected behind it. Same label the
            scoreboard, engagement and class-boards pickers use, so the four
            agree on what a class is called. */
         sel.innerHTML = this.classes
            .map((c) => `<option value="${c.class_id}">${esc(`${c.course_code} · ${c.section}`)}</option>`)
            .join("");
         this.classId = Number(this.classes[0].class_id);
         this.applyClassMode();
         this.loadGames();
      } catch {
         this.setMsg("Couldn't load your classes.", "err");
      }
   }

   async loadGames(quiet = false) {
      if (!this.classId) return;
      // A background poll on a view nobody is looking at is pure server load.
      // An explicit Refresh or a control press passes quiet=false and still runs.
      if (quiet && !this.isVisible()) return;
      const list = this.querySelector("#games-list");
      if (!quiet && !this.games.length) list.innerHTML = `<p class="empty">Loading…</p>`;
      try {
         const res = await api(`/games?class_id=${this.classId}`);
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || "failed");
         this.games = data.games || [];

         /*
            RE-RENDER ONLY WHEN SOMETHING STRUCTURAL CHANGED.

            This used to rebuild the entire list innerHTML every poll, which threw
            away and recreated the expanded results table twice a second. That is
            the flicker - and it also killed text selection, hover, and scroll
            position inside the results box while the instructor was reading it.

            The clock is deliberately NOT part of the signature: it changes every
            tick and paintClocks() already updates those numbers in place without
            touching the rest of the DOM. So a running game repaints two digits a
            second instead of the whole page.
         */
         const sig = this.games.map((g) =>
            [g.session_id, g.state, g.file_released, g.players, g.title].join(":")
         ).join("|") + `#${this.openResults ?? ""}`;
         if (sig !== this.listSig) {
            this.listSig = sig;
            this.renderList();
         } else {
            this.paintClocks();
         }
         if (this.openResults != null) this.loadResults(this.openResults);
      } catch {
         if (!quiet) this.setMsg("Couldn't load games for this class.", "err");
      }
   }

   renderList() {
      /*
         Rebuilding the list throws away the expanded results table, so the cached
         results signature no longer describes anything on screen. Drop it here,
         not just in toggleResults().

         toggleResults() already cleared it for the one case somebody thought of:
         the instructor collapsing and re-opening the same panel. But renderList()
         is also called from the POLL, whenever the list signature changes - a new
         game is created, a student joins, a game starts or stops. In that path the
         box was emptied while resultsSig still held the previous rows, so the next
         loadResults() painted "Loading...", fetched, found rowSig unchanged,
         returned early, and left the panel stuck on "Loading..." forever.

         Symptom: results appear, then a few seconds later revert to "Loading..."
         and never come back. Reported 2026-08-25 right after creating a second
         game, which is exactly what changes the signature.
      */
      this.resultsSig = null;
      const list = this.querySelector("#games-list");
      if (!this.games.length) {
         list.innerHTML = this.isTaHere()
            ? `<p class="empty">No games yet for this class. Your professor creates them; you can run them from here.</p>`
            : `<p class="empty">No games yet for this class. Create one to run a timed round.</p>`;
         return;
      }
      list.innerHTML = this.games.map((g) => this.renderCard(g)).join("");
   }

   /* What the file check said when this game was created. Hidden once the game
      is finished, and for games created before the check existed (launch null).
      A forced launch stays visible, because making the override visible
      afterwards is the whole reason it is recorded. */
   renderLaunch(g) {
      const l = g.launch;
      if (!l || (!l.forced && !(l.issues || []).length)) return "";
      if (g.state === "closed" && !l.forced) return "";
      const items = (l.issues || []).map((i) => `<li>${esc(i.message)}</li>`).join("");
      const head = l.forced
         ? `<strong>Created despite a failed file check.</strong>`
         : `<strong>File check: ${l.issues.length} warning${l.issues.length === 1 ? "" : "s"}.</strong>`;
      return `<div class="game-launch${l.forced ? " forced" : ""}">${head}
                 ${items ? `<ul class="launch-issues">${items}</ul>` : ""}</div>`;
   }

   renderCard(g) {
      const label = STATE_LABEL[g.state] || g.state;
      const urgent = g.state === "open" && g.remaining_secs <= 60 ? " game-card-urgent" : "";
      const expanded = this.openResults === g.session_id;
      return `
         <article class="game-card game-card-${esc(g.state)}${urgent}" data-session="${g.session_id}">
            <div class="game-card-head">
               <div>
                  <h2 class="game-card-title">${esc(g.title || g.assignment_title)}</h2>
                  <p class="game-card-sub">
                     ${esc(g.assignment_title)} · ${g.players} joined ·
                     <span class="game-state-pill game-state-${esc(g.state)}">${esc(label)}</span>
                  </p>
               </div>
               <div class="game-card-clock" data-clock="${g.session_id}">${fmtClock(g.remaining_secs)}</div>
            </div>

            <div class="game-card-code">
               ${g.file_released
                  ? `<span class="game-release on">File released</span>
                     <span class="game-release-hint">Students can download and open it. The clock is separate.</span>`
                  : `<span class="game-release off">File not released</span>
                     <span class="game-release-hint">Nobody can download the case yet.</span>`}
            </div>

            ${this.renderLaunch(g)}

            <div class="game-card-actions">
               ${this.renderControls(g)}
               <button class="btn-secondary" data-action="results" data-id="${g.session_id}">
                  ${expanded ? "Hide results" : "Results"}
               </button>
               ${this.hasScoreboardView() ? `
               <button class="btn-secondary" data-action="scoreboard" data-id="${g.session_id}"
                       title="Open this game's live board on the Scoreboard tab">Scoreboard</button>
               ${this.isTaHere() ? "" : `<button class="btn-secondary" data-action="obs" data-id="${g.session_id}"
                       title="Get the OBS overlay link for this game, with its countdown clock">OBS link</button>`}` : ""}
            </div>

            <div class="game-results" data-results="${g.session_id}"
                 style="${expanded ? "" : "display:none;"}"></div>
         </article>
      `;
   }

   /*
      TWO INDEPENDENT CONTROLS, because releasing the file and starting the clock
      are separate acts minutes apart:

        Release file  -> students may download and open it; the pane waits.
        Start         -> the clock runs and answers count.

      Release comes FIRST in the row and is the primary button while the file is
      still held back, because that is the next thing to press. Once released,
      Start becomes primary. Un-release stays available but is secondary - it
      cannot un-download a file somebody already has, and the label says so via
      the title attribute rather than implying a recall.
   */
   renderControls(g) {
      const b = (action, text, cls = "btn-secondary", title = "") =>
         `<button class="${cls}" data-action="${action}" data-id="${g.session_id}"${
            title ? ` title="${esc(title)}"` : ""}>${text}</button>`;

      const release = g.state === "closed" ? "" : (g.file_released
         ? b("unrelease", "Un-release file", "btn-secondary",
             "Stops further downloads. Anyone who already downloaded it keeps their copy.")
         : b("release", "Release file", "btn-primary",
             "Lets students download the case. Does not start the clock."));

      /* Delete is offered on every state EXCEPT open, where Stop comes first.
         Creating the wrong game is a one-click mistake (easy to pick the
         individual assignment instead of its team version), and a stale scheduled
         game also occupies the one-live-game-per-class slot. */
      const del = this.isTaHere() ? "" : b("delete", "Delete", "btn-danger",
                    "Removes this game. Refuses if students have joined unless you confirm again.");

      if (g.state === "scheduled") {
         // Start is only the primary action once the room actually has the file.
         return release + b("start", "Start", g.file_released ? "btn-primary" : "btn-secondary") + del;
      }
      if (g.state === "open") {
         return release + b("pause", "Pause") + b("extend", "+1 min") + b("stop", "Stop", "btn-primary");
      }
      if (g.state === "paused") {
         return release + b("resume", "Resume", "btn-primary") + b("stop", "Stop") + del;
      }
      return del;   // closed: nothing left to drive, but it can still be cleared away
   }

   /*
      Delete is not a state transition, so it does not go through transition():
      different verb, a confirm before it fires, and a two-step path when the game
      carries history. The server refuses a game with participants or score rows
      unless force=1, and reports the counts, so the second prompt can say exactly
      what is about to be discarded rather than asking "are you sure" twice.
   */
   async deleteGame(sessionId, btn) {
      const g = this.games.find((x) => x.session_id === sessionId);
      const name = g ? g.title : `game ${sessionId}`;
      if (!window.confirm(`Delete "${name}"?\n\nThis cannot be undone.`)) return;

      btn.disabled = true;
      this.setMsg("");
      try {
         let res = await api(`/games/${sessionId}`, { method: "DELETE" });
         let data = await res.json();

         if (!res.ok && data.reason === "game_has_history") {
            const msg = `"${name}" has ${data.participants} student(s) joined and `
                      + `${data.scores} saved score row(s).\n\nDeleting it discards that `
                      + `record. The students' work stays on the platform as ungamed `
                      + `practice, but the game itself and who joined it are gone.\n\n`
                      + `Delete anyway?`;
            if (!window.confirm(msg)) { btn.disabled = false; return; }
            res = await api(`/games/${sessionId}?force=1`, { method: "DELETE" });
            data = await res.json();
         }

         if (!res.ok || !data.ok) {
            const nice = {
               not_authorized: "You do not teach this class.",
               cannot_delete_when_open: "That game is running. Press Stop first, then delete it.",
               not_found: "That game no longer exists.",
            }[data.reason] || "Couldn't delete the game.";
            throw new Error(nice);
         }

         if (this.openResults === sessionId) this.openResults = null;
         this.setMsg(`Deleted "${name}".`, "ok");
         await this.loadGames();
      } catch (e) {
         btn.disabled = false;
         this.setMsg(e.message || "Couldn't delete the game.", "err");
      }
   }

   /* Repaint only the clocks, so the local tick does not blow away an expanded
      results table or fight with a button the instructor is about to press. */
   paintClocks() {
      for (const g of this.games) {
         const el = this.querySelector(`[data-clock="${g.session_id}"]`);
         if (el) el.textContent = fmtClock(g.remaining_secs);
      }
   }

   async transition(sessionId, action, btn) {
      const body = action === "extend" ? { seconds: 60 } : {};
      btn.disabled = true;
      this.setMsg("");
      try {
         const res = await api(`/games/${sessionId}/${action}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
         });
         const data = await res.json();
         if (!res.ok || !data.ok) {
            const nice = {
               not_authorized: "You do not teach this class.",
               ta_not_permitted: "This class does not let TAs run games.",
               cannot_start_when_open: "That game is already running.",
               cannot_pause_when_closed: "That game has already finished.",
               cannot_extend_when_closed: "That game has already finished.",
               cannot_release_when_closed: "That game has already finished.",
               game_already_live: data.detail || "This class already has a game on that assignment.",
            }[data.reason] || `Couldn't ${action} the game.`;
            throw new Error(nice);
         }
         await this.loadGames(true);
         if (action === "stop") this.setMsg("Game stopped. Scores are locked in.", "ok");
      } catch (err) {
         this.setMsg(err.message || `Couldn't ${action} the game.`, "err");
      } finally {
         btn.disabled = false;
      }
   }

   async copyCode(code, btn) {
      try {
         await navigator.clipboard.writeText(code);
         const was = btn.textContent;
         btn.textContent = "Copied";
         window.setTimeout(() => { btn.textContent = was; }, 1500);
      } catch {
         this.setMsg(`Join code: ${code}`, "ok");
      }
   }

   toggleResults(sessionId) {
      this.openResults = this.openResults === sessionId ? null : sessionId;
      // renderList() destroys the results box, so the cached signature no longer
      // describes anything on screen. Without this, re-opening the SAME session
      // matches the stale signature, loadResults returns early, and the panel
      // sits on "Loading..." forever.
      this.resultsSig = null;
      this.listSig = null;
      this.renderList();
      if (this.openResults != null) this.loadResults(this.openResults);
   }

   async loadResults(sessionId) {
      const box = this.querySelector(`[data-results="${sessionId}"]`);
      if (!box) return;
      if (!box.innerHTML) box.innerHTML = `<p class="empty">Loading…</p>`;
      try {
         const res = await api(`/games/${sessionId}/results${this.reveal ? "?reveal=1" : ""}`);
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || "failed");
         const rows = data.results || [];
         if (!rows.length) {
            box.innerHTML = `<p class="empty">Nobody has joined this game yet.</p>`;
            return;
         }
         const finished = data.game && data.game.state === "closed";
         const teams = data.team_mode ? (data.teams || []) : [];

         /*
            Same reason as the list above: only touch the DOM when the rendered
            content would actually differ. During a live game this table is
            re-fetched every poll, and most polls return identical rows - a
            student who has not answered anything since the last one. Replacing
            innerHTML anyway made the results panel strobe.
         */
         const rowSig = JSON.stringify(rows) + JSON.stringify(teams)
            + `|${finished}|${this.reveal}|${[...this.openTeams].sort().join(",")}`;
         if (rowSig === this.resultsSig) return;
         this.resultsSig = rowSig;

         /*
            TEAM GAME (2026-08-29). The team is the competitor, so it is the
            table; individual scores are still here but nested one level down,
            which is where they belong on a team case. An individual game is
            unaffected - teams is empty and this branch is not taken.
         */
         const byUser = new Map(rows.map((r) => [Number(r.user_id), r]));
         const table = teams.length
            ? `
            <table class="cd-table game-results-table">
               <thead>
                  <tr>
                     <th>
                        Team
                        <label class="reveal-toggle reveal-inline"
                               title="Off by default so the panel is safe to project. Resets on reload.">
                           <input type="checkbox" data-action="reveal"
                                  data-id="${sessionId}"${this.reveal ? " checked" : ""}>
                           <span>real names</span>
                        </label>
                     </th>
                     <th>${finished ? "Game score" : "Score so far"}</th>
                     ${finished ? "<th>Practice score now</th>" : ""}
                     <th>Last activity</th>
                  </tr>
               </thead>
               <tbody>
                  ${teams.map((t) => this.renderTeamRows(t, byUser, finished, sessionId)).join("")}
               </tbody>
            </table>`
            : `
            <table class="cd-table game-results-table">
               <thead>
                  <tr>
                     <th>
                        Student
                        <label class="reveal-toggle reveal-inline"
                               title="Off by default so the panel is safe to project. Resets on reload.">
                           <input type="checkbox" data-action="reveal"
                                  data-id="${sessionId}"${this.reveal ? " checked" : ""}>
                           <span>real names</span>
                        </label>
                     </th>
                     <th>${finished ? "Game score" : "Score so far"}</th>
                     ${finished ? "<th>Practice score now</th>" : ""}
                     <th>Last activity</th>
                  </tr>
               </thead>
               <tbody>
                  ${rows.map((r) => this.renderResultRow(r, finished)).join("")}
               </tbody>
            </table>`;

         box.innerHTML = `
            ${table}
            ${finished ? `<p class="tv-desc">
               The game score is frozen at the buzzer. The practice column keeps moving
               if a student carries on working, so a gap between the two means they
               finished the case after time was called.</p>` : ""}
            ${teams.length ? `<p class="tv-desc">
               A team's score is not the sum of its members. A question where teammates
               hold different answers at the buzzer scores zero for the team even if one
               of them had it right, so a team can score below its strongest member.
               Expand a team to see who contributed what.</p>` : ""}
         `;
      } catch {
         box.innerHTML = `<p class="msg err">Couldn't load results.</p>`;
      }
   }

   /*
   One team, plus its members when expanded.

   The member rows are the SAME per-student rows the individual table renders,
   looked up out of `results` by user_id, so the two views can never disagree
   about a student's score.

   A member id with no matching results row is skipped rather than rendered
   blank: after the buzzer member_ids is the frozen roster, and someone on the
   roster who never joined the game genuinely has no result to show.
   */
   renderTeamRows(t, byUser, finished, sessionId) {
      const open = this.openTeams.has(Number(t.team_id));
      const pts = (earned, total) =>
         earned == null ? "—" : `${Number(earned)}${total != null ? ` / ${Number(total)}` : ""}`;
      const when = t.last_activity
         ? new Date(t.last_activity).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
         : "—";
      const gameCol = finished ? pts(t.game_points, t.game_total) : pts(t.live_points, t.live_total);
      const liveCol = finished ? `<td>${pts(t.live_points, t.live_total)}</td>` : "";

      const ids = Array.isArray(t.member_ids) ? t.member_ids : [];
      const members = ids.map((id) => byUser.get(Number(id))).filter(Boolean);
      const cols = finished ? 4 : 3;

      const head = `
         <tr class="team-row">
            <td>
               <button class="team-toggle" data-action="team"
                       data-id="${t.team_id}" data-session="${sessionId}"
                       aria-expanded="${open}">
                  <span class="team-caret">${open ? "▾" : "▸"}</span>
                  ${esc(t.team_name || `Team ${t.team_id}`)}
               </button>
               <span class="team-count">${members.length} player${members.length === 1 ? "" : "s"}</span>
            </td>
            <td>${gameCol}</td>${liveCol}<td>${when}</td>
         </tr>`;

      if (!open) return head;
      if (!members.length) {
         return head + `<tr class="team-member"><td colspan="${cols}" class="empty">
            No members of this team joined the game.</td></tr>`;
      }
      return head + members
         .map((r) => this.renderResultRow(r, finished, true))
         .join("");
   }

   toggleTeam(teamId, sessionId) {
      if (this.openTeams.has(teamId)) this.openTeams.delete(teamId);
      else this.openTeams.add(teamId);
      // resultsSig folds in openTeams, so this repaints rather than being
      // swallowed by the identical-rows cache.
      this.loadResults(sessionId);
   }

   renderResultRow(r, finished, nested = false) {
      // `name` is the server's safe label and is ALWAYS present (nickname, else
      // "Student 42"). display_name/email arrive as null unless reveal=1, so the
      // real-name branch is only ever taken when the instructor asked for it.
      const who = esc(r.display_name || r.email || r.name || `User ${r.user_id}`);
      const pts = (earned, total) =>
         earned == null ? "—" : `${Number(earned)}${total != null ? ` / ${Number(total)}` : ""}`;
      const when = r.last_activity
         ? new Date(r.last_activity).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
         : "—";
      // Before the buzzer there is no frozen score yet, so the live number IS the
      // game score and showing an empty column would read as "scored nothing".
      const gameCol = finished ? pts(r.game_points, r.game_total) : pts(r.live_points, r.live_total);
      const liveCol = finished ? `<td>${pts(r.live_points, r.live_total)}</td>` : "";
      // `nested` is the only difference between a standalone student row and the
      // same row shown under its team: a class for the indent, nothing else, so
      // the two can never report different numbers.
      const cls = nested ? ` class="team-member"` : "";
      return `<tr${cls}><td>${who}</td><td>${gameCol}</td>${liveCol}<td>${when}</td></tr>`;
   }

   /* The launch check refused, and the instructor has seen why. Arm "Create
      anyway" for exactly this assignment. */
   armForce(assignmentId) {
      this.forceFor = assignmentId;
      this.querySelector("#btn-create-game").textContent = "Create anyway";
   }

   disarmForce() {
      this.forceFor = null;
      const btn = this.querySelector("#btn-create-game");
      if (btn) btn.textContent = "Create";
      const err = this.querySelector("#new-game-error");
      if (err) err.textContent = "";
   }

   async openNewGame() {
      const err = this.querySelector("#new-game-error");
      err.textContent = "";
      this.disarmForce();
      // Attach-on-close is the default for every new game (2026-10-03); an
      // untick on the previous game must not carry over to this one.
      this.querySelector("#ng-attach").checked = true;
      const sel = this.querySelector("#ng-assignment");
      sel.innerHTML = `<option value="">Loading…</option>`;
      this.dialog.showModal();
      try {
         const res = await api("/assignments-list");
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || "failed");
         this.assignments = data.assignments || [];
         sel.innerHTML = this.assignments.length
            ? this.assignments.map((a) =>
               `<option value="${a.assignment_id}">${esc(a.title || `#${a.assignment_id}`)}</option>`).join("")
            : `<option value="">No assignments yet</option>`;
      } catch {
         sel.innerHTML = `<option value="">Couldn't load assignments</option>`;
      }
   }

   async onCreate(e) {
      e.preventDefault();
      const err = this.querySelector("#new-game-error");
      const btn = this.querySelector("#btn-create-game");
      err.textContent = "";

      const assignmentId = Number(this.querySelector("#ng-assignment").value);
      const minutes = Number(this.querySelector("#ng-minutes").value);
      if (!Number.isInteger(assignmentId) || !assignmentId) {
         err.textContent = "Pick an assignment.";
         return;
      }
      if (!Number.isFinite(minutes) || minutes <= 0) {
         err.textContent = "Give the game a length in minutes.";
         return;
      }

      const force = this.forceFor === assignmentId;
      btn.disabled = true;
      btn.textContent = "Creating…";
      try {
         const res = await api(force ? "/games?force=1" : "/games", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
               assignment_id: assignmentId,
               class_id: this.classId,
               duration_secs: Math.round(minutes * 60),
               title: this.querySelector("#ng-title").value.trim() || undefined,
               lock_mode: this.querySelector("#ng-lock").value,
               attach_on_close: this.querySelector("#ng-attach").checked,
            }),
         });
         const data = await res.json();

         /* THE LAUNCH CHECK REFUSED (2026-09-10). Say exactly why, in the
            server's words, list whatever else it found, and turn the button
            into "Create anyway". The override is recorded on the game. */
         if (res.status === 409 && data.reason === "launch_check_failed" && data.check) {
            const c = data.check;
            const li = (i) => `<li>${esc(i.message)}</li>`;
            err.innerHTML =
               `<strong>This file isn't ready for this game.</strong>`
               + `<ul class="launch-issues">${(c.blocking || []).map(li).join("")}</ul>`
               + ((c.warnings || []).length
                  ? `<p>Also:</p><ul class="launch-issues launch-warn">${c.warnings.map(li).join("")}</ul>`
                  : "")
               + `<p>You can still create it. If you do, the override is saved with the game.</p>`;
            this.armForce(assignmentId);
            return;
         }

         if (!res.ok || !data.ok) {
            const nice = {
               not_authorized: "You do not teach this class.",
               assignment_not_found: "That assignment no longer exists.",
               invalid_duration: "Give the game a length in minutes.",
            }[data.reason] || "Couldn't create the game.";
            throw new Error(nice);
         }
         this.dialog.close();
         this.disarmForce();
         const warnings = (data.check && data.check.warnings) || [];
         const forcedNote = data.check && data.check.forced ? " Created despite the file check." : "";
         this.setMsg(
            `Created "${data.game.title}". Join code ${data.game.join_code}. ` +
            `It is not running yet. Press Start when the room is ready.` + forcedNote +
            (warnings.length
               ? ` Before you start: ${warnings.map((w) => w.message).join(" ")}`
               : ""),
            warnings.length || forcedNote ? "warn" : "ok");
         this.loadGames();
      } catch (e2) {
         err.textContent = e2.message || "Couldn't create the game.";
      } finally {
         btn.disabled = false;
         btn.textContent = this.forceFor === assignmentId ? "Create anyway" : "Create";
      }
   }
}

customElements.define("app-games", AppGames);
